import { useCallback, useEffect, useState } from 'react';
import {
  ChakraBox,
  ChakraFlex,
  CircularProgress,
  Counter,
  EmptyListV2,
  FAIconName,
  Text,
  ToastMessage,
  useToastMessage,
} from '@equip.health/ui';
import { DEFAULT_PAGINATION_CONFIG } from '~/lib/constants';
import { MaudPermissions } from '~/lib/constants/permissions.constants';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import usePortalAccess from '~/lib/hooks/usePortalAccess';
import { Path } from '~/routes/constants';
import {
  ScriptSureSyncFailure,
  useGetScriptSureSyncFailuresQuery,
  useReSyncScriptSurePatientMutation,
} from '~/store/services/integration';
import ScriptSureSyncTable from './scriptsure-sync/ScriptSureSyncTable';
import { SCRIPTSURE_SYNC_CONSTANTS } from './scriptsure-sync/scriptSureSync.constants';

const {
  EMPTY_STATE_MESSAGE,
  LOAD_ERROR_MESSAGE,
  MAUD_ACCESS_ERROR_MESSAGE,
  RESYNC_ERROR_MESSAGE,
  RESYNC_SUCCESS_MESSAGE,
  TITLE,
  TOP_LEVEL_ID,
} = SCRIPTSURE_SYNC_CONSTANTS;

const ScriptSureSyncDashboard = () => {
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(
    DEFAULT_PAGINATION_CONFIG.pageSize,
  );
  const [resyncingPatientIds, setResyncingPatientIds] = useState<Set<string>>(
    () => new Set(),
  );

  const { data, isError, isLoading, isFetching } =
    useGetScriptSureSyncFailuresQuery({
      pageNumber,
      pageSize: itemsPerPage,
    });
  const [reSyncPatient] = useReSyncScriptSurePatientMutation();
  const { createToast } = useToastMessage();
  const { maud: hasMaudAccess } = usePortalAccess();
  const { permissions } = useGlobalContext();
  const canNavigateToPatientChart =
    hasMaudAccess && !!permissions[MaudPermissions.MAUD_PATIENT_DASHBOARD];

  const totalCount = data?.totalCount ?? 0;
  const lastPage = Math.max(1, Math.ceil(totalCount / itemsPerPage));

  useEffect(() => {
    if (!isFetching && data && pageNumber > lastPage) {
      setPageNumber(lastPage);
    }
  }, [data, isFetching, lastPage, pageNumber]);

  const handlePageChange = (page: number) => {
    setPageNumber(page);
  };

  const handleItemPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setPageNumber(1);
  };

  const handleResync = useCallback(
    async (failure: ScriptSureSyncFailure) => {
      setResyncingPatientIds((ids) => new Set(ids).add(failure.patientId));
      try {
        const { isSynced } = await reSyncPatient(failure.patientId).unwrap();
        if (isSynced) {
          createToast({
            icon: FAIconName.checkRegular,
            id: `${TOP_LEVEL_ID}__resync-success`,
            text: RESYNC_SUCCESS_MESSAGE,
            variant: 'success',
          });
          return;
        }
        if (canNavigateToPatientChart) {
          window.open(
            `${Path.MAUD_PATIENTS}/${failure.patientId}/profile`,
            '_blank',
            'noopener,noreferrer',
          );
          return;
        }
        createToast({
          icon: FAIconName.exclamationTriangleRegular,
          id: `${TOP_LEVEL_ID}__maud-access-error`,
          text: MAUD_ACCESS_ERROR_MESSAGE,
          variant: 'critical',
        });
      } catch {
        createToast({
          icon: FAIconName.exclamationTriangleRegular,
          id: `${TOP_LEVEL_ID}__resync-error`,
          text: RESYNC_ERROR_MESSAGE,
          variant: 'critical',
        });
      } finally {
        setResyncingPatientIds((ids) => {
          const nextIds = new Set(ids);
          nextIds.delete(failure.patientId);
          return nextIds;
        });
      }
    },
    [canNavigateToPatientChart, createToast, reSyncPatient],
  );

  const renderContent = () => {
    if (isError) {
      return (
        <ChakraBox marginTop="60px">
          <ToastMessage
            icon={FAIconName.exclamationTriangleRegular}
            id={`${TOP_LEVEL_ID}__load-error`}
            isFullWidth
            text={LOAD_ERROR_MESSAGE}
            variant="critical"
          />
        </ChakraBox>
      );
    }
    if (!totalCount) {
      return (
        <ChakraBox marginTop="60px">
          <EmptyListV2 id={TOP_LEVEL_ID} primaryText={EMPTY_STATE_MESSAGE} />
        </ChakraBox>
      );
    }
    return (
      <ScriptSureSyncTable
        failures={data?.data ?? []}
        handleItemPerPageChange={handleItemPerPageChange}
        handlePageChange={handlePageChange}
        id={TOP_LEVEL_ID}
        isLoading={isFetching}
        onResync={handleResync}
        pageNumber={pageNumber}
        resyncingPatientIds={resyncingPatientIds}
        totalCount={totalCount}
      />
    );
  };

  return (
    <ChakraFlex
      direction="column"
      gap="20px"
      id={TOP_LEVEL_ID}
      padding="30px 0 30px 6px"
      width="100%"
    >
      <ChakraFlex alignItems="center" gap="12px">
        <Text variant="h3">{TITLE}</Text>
        {isLoading || isFetching ? (
          <CircularProgress
            id={`${TOP_LEVEL_ID}__loader`}
            isIndeterminate
            padding="8px"
            size="30px"
          />
        ) : (
          !isError && <Counter number={totalCount} />
        )}
      </ChakraFlex>
      {!isLoading && renderContent()}
    </ChakraFlex>
  );
};

export default ScriptSureSyncDashboard;
